import { calcularPerfilCobranzaV2 } from "./cobranzaInteligenteV2";
import * as agendaCobranza from "./agendaCobranza";
import { toJsDate } from "./fechas";

const toDateSafe = (value) => toJsDate(value);

const getPagosArray = (data = {}) => {
  if (Array.isArray(data.pagos)) return data.pagos;
  if (data.pagos && typeof data.pagos === "object") return Object.values(data.pagos);
  return [];
};

const isSameDay = (a, b) => {
  const da = toDateSafe(a);
  const db = toDateSafe(b);
  if (!da || !db) return false;

  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

const isSameMonth = (a, b) => {
  const da = toDateSafe(a);
  const db = toDateSafe(b);
  if (!da || !db) return false;

  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth();
};

const endOfWeek = (date) => {
  const d = agendaCobranza.startOfDay(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() + (7 - day));
  d.setHours(23, 59, 59, 999);
  return d;
};

const isBetween = (value, start, end) => {
  const d = toDateSafe(value);
  if (!d) return false;
  return d.getTime() >= start.getTime() && d.getTime() <= end.getTime();
};

const getClienteNombre = (data = {}) =>
  [
    data.clienteNombre,
    data.clienteApellidos,
    data.nombreCliente,
    data.cliente,
  ]
    .filter(Boolean)
    .join(" ")
    .trim() || "Cliente sin nombre";

const crearItemDeuda = (docSnap, hoy) => {
  const data = docSnap.data ? docSnap.data() : docSnap;
  const id = docSnap.id || data.id;
  const perfil = calcularPerfilCobranzaV2(data, hoy);
  const pagos = getPagosArray(data);
  const nextDueDate = agendaCobranza.getDueTimestamp(data);
  const mensualidad = agendaCobranza.getMensualidadMostrable(data);
  const saldoPendiente = agendaCobranza.calcularSaldoMostrable(data);

  // "Cobrado" = solo pagos positivos; las devoluciones/ajustes (cantidad < 0)
  // no restan aquí (evita KPIs negativos por devoluciones grandes del mes).
  const cobradoHoy = pagos.reduce((acc, p) => {
    const c = agendaCobranza.toNumber(p?.cantidad);
    return acc + (c > 0 && isSameDay(p?.fecha, hoy) ? c : 0);
  }, 0);

  const cobradoMes = pagos.reduce((acc, p) => {
    const c = agendaCobranza.toNumber(p?.cantidad);
    return acc + (c > 0 && isSameMonth(p?.fecha, hoy) ? c : 0);
  }, 0);

  return {
    id,
    ...data,
    clienteNombre: getClienteNombre(data),
    perfil,
    pagos,
    nextDueDate,
    mensualidad,
    saldoPendiente,
    cobradoHoy,
    cobradoMes,
  };
};

export const construirResumenCobranza = (deudasDocs = [], hoy = new Date()) => {
  const hoyInicio = agendaCobranza.startOfDay(hoy);
  const semanaFin = endOfWeek(hoy);
  const deudas = deudasDocs
    .map((docSnap) => crearItemDeuda(docSnap, hoy))
    .filter((deuda) => deuda.perfil?.esCobrable === true);

  const resumen = deudas.reduce(
    (acc, deuda) => {
      const perfil = deuda.perfil || {};
      const esCobrable = perfil.esCobrable === true;
      const saldoAtrasado = esCobrable ? Number(perfil.saldoAtrasado || 0) : 0;
      const mesesAtraso = Number(perfil.mesesAtraso || 0);
      const saldoPendiente = Number(perfil.saldoRestante ?? deuda.saldoPendiente ?? 0);
      const mensualidad = Number(perfil.mensualidad || deuda.mensualidad || 0);

      acc.cobradoHoy += deuda.cobradoHoy;
      acc.cobradoMes += deuda.cobradoMes;

      if (esCobrable && saldoPendiente > 0) {
        acc.saldoPendiente += saldoPendiente;
      }

      if (esCobrable && mesesAtraso >= 1 && saldoAtrasado > 0) {
        acc.cuentasEnMora += 1;
        acc.totalMoraReal += saldoAtrasado;
        acc.potencialRecuperable += Number(perfil.pagoIdeal || perfil.pagoMinimo || 0);
      }

      if (esCobrable && (perfil.prioridad === "critica" || Number(perfil.prioridadScore || 0) >= 75)) {
        acc.cuentasCriticas += 1;
      }

      if (deuda.nextDueDate && isSameDay(deuda.nextDueDate, hoy) && esCobrable) {
        acc.porCobrarHoy += Math.min(mensualidad, saldoPendiente);
      }

      if (
        deuda.nextDueDate &&
        isBetween(deuda.nextDueDate, hoyInicio, semanaFin) &&
        esCobrable
      ) {
        acc.porCobrarSemana += Math.min(mensualidad, saldoPendiente);
      }

      const cartera = perfil.cartera || "AL_CORRIENTE";
      if (cartera === "A") acc.moraA += 1;
      else if (cartera === "B") acc.moraB += 1;
      else if (cartera === "C") acc.moraC += 1;
      else if (cartera === "D") acc.moraD += 1;
      else if (cartera === "COLA_FINAL") acc.colaFinal += 1;
      else acc.alCorriente += 1;

      // ERP-R4: aging de cartera por antigüedad de mora. Como el sistema es de
      // mensualidades, los buckets de días del roadmap se mapean a meses de
      // atraso financiero: vigente (<1 mes ≈ al corriente), 1-30d (1 mes),
      // 31-60d (2), 61-90d (3), +90d (4+). Cada bucket acumula # de cuentas,
      // saldo pendiente y monto en mora (saldoAtrasado).
      if (esCobrable && saldoPendiente > 0) {
        const bucket =
          mesesAtraso < 1 ? "vigente"
          : mesesAtraso < 2 ? "d1_30"
          : mesesAtraso < 3 ? "d31_60"
          : mesesAtraso < 4 ? "d61_90"
          : "d90plus";
        acc.aging[bucket].cuentas += 1;
        acc.aging[bucket].saldoPendiente += saldoPendiente;
        acc.aging[bucket].mora += saldoAtrasado;
      }

      return acc;
    },
    {
      cuentasEnMora: 0,
      totalMoraReal: 0,
      saldoPendiente: 0,
      cobradoHoy: 0,
      cobradoMes: 0,
      porCobrarHoy: 0,
      porCobrarSemana: 0,
      cuentasCriticas: 0,
      potencialRecuperable: 0,
      alCorriente: 0,
      moraA: 0,
      moraB: 0,
      moraC: 0,
      moraD: 0,
      colaFinal: 0,
      // ERP-R4: buckets de aging (inicializados aquí para que el reduce sume)
      aging: {
        vigente: { cuentas: 0, saldoPendiente: 0, mora: 0 },
        d1_30: { cuentas: 0, saldoPendiente: 0, mora: 0 },
        d31_60: { cuentas: 0, saldoPendiente: 0, mora: 0 },
        d61_90: { cuentas: 0, saldoPendiente: 0, mora: 0 },
        d90plus: { cuentas: 0, saldoPendiente: 0, mora: 0 },
      },
    }
  );

  const conMora = deudas.filter(
    (d) =>
      d.perfil?.esCobrable &&
      Number(d.perfil?.mesesAtraso || 0) >= 1 &&
      Number(d.perfil?.saldoAtrasado || 0) > 0
  );

  const rankings = {
    mayorMora: [...conMora]
      .sort(
        (a, b) =>
          Number(b.perfil?.saldoAtrasado || 0) - Number(a.perfil?.saldoAtrasado || 0) ||
          Number(b.perfil?.mesesAtraso || 0) - Number(a.perfil?.mesesAtraso || 0)
      )
      .slice(0, 5),
    mayorRecuperabilidad: [...conMora]
      .sort(
        (a, b) =>
          Number(b.perfil?.recuperabilidadScore || 0) - Number(a.perfil?.recuperabilidadScore || 0) ||
          Number(b.perfil?.pagoIdeal || 0) - Number(a.perfil?.pagoIdeal || 0)
      )
      .slice(0, 5),
    cierreRapido: [...deudas]
      .filter((d) => d.perfil?.esCobrable && d.perfil?.oportunidad === "cierre_rapido")
      .sort((a, b) => Number(b.perfil?.oportunidadScore || 0) - Number(a.perfil?.oportunidadScore || 0))
      .slice(0, 5),
    criticas: [...conMora]
      .filter((d) => d.perfil?.prioridad === "critica" || Number(d.perfil?.prioridadScore || 0) >= 75)
      .sort((a, b) => Number(b.perfil?.prioridadScore || 0) - Number(a.perfil?.prioridadScore || 0))
      .slice(0, 5),
  };

  const alertas = [
    resumen.cuentasEnMora > 0 && {
      tipo: "Mora real",
      texto: `${resumen.cuentasEnMora} cuentas con atraso real`,
      nivel: resumen.cuentasEnMora >= 8 ? "bad" : "warn",
    },
    resumen.cuentasCriticas > 0 && {
      tipo: "Criticas",
      texto: `${resumen.cuentasCriticas} cuentas requieren atencion`,
      nivel: "bad",
    },
    resumen.potencialRecuperable > 0 && {
      tipo: "Potencial",
      texto: `Hasta ${agendaCobranza.formatMoney(resumen.potencialRecuperable)} recuperables`,
      nivel: "warn",
    },
  ].filter(Boolean);

  return {
    deudas,
    resumen,
    cartera: {
      alCorriente: resumen.alCorriente,
      moraA: resumen.moraA,
      moraB: resumen.moraB,
      moraC: resumen.moraC,
      moraD: resumen.moraD,
    },
    rankings,
    alertas,
  };
};
